import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

import { Timeout } from '@utils/types';

const throttleMap: Record<string, true | Timeout> = {};
const throttleMs = 1000;
export const throttleErrorMessage = 'Request throttled';

const getHashed = (mutationFn: Function, variables: unknown) => {
  if (variables instanceof FormData) {
    variables = [...variables.entries()];
  }
  const stringified = JSON.stringify({ fn: mutationFn.toString(), variables: variables ?? '' });
  const split = stringified.split('');
  const hash = split.reduce((accumulated, current) => {
    const charCode = current.charCodeAt(0);
    return (accumulated << 5) - accumulated + charCode;
  }, 0);
  return (hash >>> 0).toString(36).padStart(7, '0');
};

export function useThrottledMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext>;

export function useThrottledMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    mutationFn: (variables: TVariables) => Promise<TData>;
  }
): UseMutationResult<TData, TError, TVariables, TContext>;

export function useThrottledMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFnOrOptions:
    | ((variables: TVariables) => Promise<TData>)
    | (UseMutationOptions<TData, TError, TVariables, TContext> & {
        mutationFn: (variables: TVariables) => Promise<TData>;
      }),
  options?: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutationFn =
    typeof mutationFnOrOptions === 'function'
      ? mutationFnOrOptions
      : mutationFnOrOptions.mutationFn;

  const finalOptions: UseMutationOptions<TData, TError, TVariables, TContext> =
    (typeof mutationFnOrOptions === 'object' && !options ? mutationFnOrOptions : options) ?? {};

  const { onError, ...restOptions } = finalOptions;

  const throttledMutation = useMutation<TData, TError, TVariables, TContext>({
    ...restOptions,
    mutationFn: async (variables: TVariables) => {
      const hashed = getHashed(mutationFn, variables);
      const isThrottled = throttleMap.hasOwnProperty(hashed);

      if (isThrottled) {
        return Promise.reject(throttleErrorMessage);
      }

      throttleMap[hashed] = true;

      try {
        const result = await mutationFn(variables);
        return result;
      } catch (e) {
        onError?.(e as TError, variables, {} as TContext);
        throw e;
      } finally {
        throttleMap[hashed] = setTimeout(() => {
          delete throttleMap[hashed];
        }, throttleMs);
      }
    },
  });

  return throttledMutation;
}
